import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ConversationList from './components/ConversationList';
import ChatWindow from './components/ChatWindow';
import StatusIndicator from './components/StatusIndicator';
import Login from './components/Login';
import APIKeyManager from './components/APIKeyManager';
import ToolInstanceManager from './components/ToolInstanceManager';
import WaterfallBuilder from './components/WaterfallBuilder';

const API_BASE = '/api';
const THEME_STORAGE_KEY = 'waterfall-ai-theme';

// Get CSRF token from cookie
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

function getSystemTheme() {
  if (typeof window === 'undefined') {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// Set axios defaults for CSRF
axios.defaults.xsrfHeaderName = 'X-CSRFToken';
axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.withCredentials = true;

// Add interceptor to ensure CSRF token is always sent
axios.interceptors.request.use(function (config) {
  const csrfToken = getCookie('csrftoken');
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  return config;
}, function (error) {
  return Promise.reject(error);
});

function App() {
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [ollamaStatus, setOllamaStatus] = useState({ connected: false, models: [] });
  const [activeTab, setActiveTab] = useState(null); // null, 'api-keys', 'tools', 'waterfalls'
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [user, setUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }

    try {
      const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      return storedTheme || getSystemTheme();
    } catch (error) {
      return getSystemTheme();
    }
  });
  const [focusedToolId, setFocusedToolId] = useState(null);
  const [toolEditId, setToolEditId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close the mobile drawer whenever the viewport grows back to desktop width
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const desktopQuery = window.matchMedia('(min-width: 769px)');
    const handleViewportChange = (event) => {
      if (event.matches) {
        setSidebarOpen(false);
      }
    };

    desktopQuery.addEventListener('change', handleViewportChange);
    return () => desktopQuery.removeEventListener('change', handleViewportChange);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    document.documentElement.setAttribute('data-theme', theme);
    document.body.classList.remove('light-mode', 'dark-mode');
    document.body.classList.add(`${theme}-mode`);
  }, [theme]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handlePreferenceChange = (event) => {
      try {
        const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
        if (!storedTheme) {
          setTheme(event.matches ? 'dark' : 'light');
        }
      } catch (error) {
        setTheme(event.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handlePreferenceChange);
    return () => mediaQuery.removeEventListener('change', handlePreferenceChange);
  }, []);

  const handleNavigateToTool = (toolId) => {
    if (!toolId) {
      return;
    }
    setFocusedToolId(toolId);
    setToolEditId(toolId);
    setActiveTab('tools');
  };

  const handleThemeToggle = (event) => {
    const shouldResetToSystem = event?.shiftKey;

    if (shouldResetToSystem) {
      try {
        localStorage.removeItem(THEME_STORAGE_KEY);
      } catch (error) {
        // Ignore storage removal errors
      }
      setTheme(getSystemTheme());
      return;
    }

    setTheme((prevTheme) => {
      const nextTheme = prevTheme === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
      } catch (error) {
        // Ignore storage write errors
      }
      return nextTheme;
    });
  };

  useEffect(() => {
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (user) {
      loadConversations();
      checkOllamaStatus();
    }
  }, [user]);

  const checkAuthentication = async () => {
    try {
      const response = await axios.get(`${API_BASE}/auth/current-user/`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_BASE}/auth/logout/`);
      // Clear all state
      setUser(null);
      setConversations([]);
      setCurrentConversation(null);
      setActiveTab(null);
      setSelectedModel('');
      // Reload page to ensure clean session
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local state and reload
      setUser(null);
      window.location.reload();
    }
  };

  const checkOllamaStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/ollama-status/`);
      setOllamaStatus(response.data);
      if (response.data.models.length > 0 && !selectedModel) {
        setSelectedModel(response.data.models[0].name);
      }
    } catch (error) {
      console.error('Error checking Ollama status:', error);
    }
  };

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API_BASE}/conversations/`);
      setConversations(response.data.results || response.data);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const createNewConversation = async () => {
    try {
      const response = await axios.post(`${API_BASE}/conversations/`, {
        title: `New Chat ${new Date().toLocaleString()}`
      });
      setConversations([response.data, ...conversations]);
      setCurrentConversation(response.data);
      setSidebarOpen(false);
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const selectConversation = async (conversationId) => {
    try {
      const response = await axios.get(`${API_BASE}/conversations/${conversationId}/`);
      setCurrentConversation(response.data);
      // Close any active tab when selecting a conversation
      setActiveTab(null);
      // Close the mobile drawer after picking a conversation
      setSidebarOpen(false);
      
      // Set model selector to the last model used in this conversation
      if (response.data.last_model) {
        setSelectedModel(response.data.last_model);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const deleteConversation = async (conversationId) => {
    try {
      await axios.delete(`${API_BASE}/conversations/${conversationId}/`);
      setConversations(conversations.filter(c => c.id !== conversationId));
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const updateConversationTitle = async (conversationId, newTitle) => {
    try {
      console.log('Updating conversation title:', conversationId, newTitle);
      const response = await axios.patch(`${API_BASE}/conversations/${conversationId}/`, {
        title: newTitle
      });
      console.log('Update response:', response.data);
      
      // Update the current conversation if it's the one being renamed
      if (currentConversation?.id === conversationId) {
        setCurrentConversation({ ...currentConversation, title: newTitle });
      }
      
      // Reload conversations list
      await loadConversations();
    } catch (error) {
      console.error('Error updating conversation title:', error);
      console.error('Error details:', error.response?.data);
      alert('Failed to update conversation title: ' + (error.response?.data?.error || error.message));
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className={`app-container ${theme === 'dark' ? 'app-container--dark' : 'app-container--light'}`}>
      {/* Top Navigation Bar with Tabs */}
      <div className="top-nav">
        <div className="nav-left">
          <button
            type="button"
            className="btn-hamburger"
            onClick={() => setSidebarOpen((open) => !open)}
            aria-label={sidebarOpen ? 'Close conversations' : 'Open conversations'}
            aria-expanded={sidebarOpen}
          >
            <i className={`fas ${sidebarOpen ? 'fa-xmark' : 'fa-bars'}`} aria-hidden="true"></i>
          </button>
          <h1 className="app-title">Waterfall AI</h1>
        </div>

        <div className="nav-tabs">
          <button
            className={`nav-tab ${activeTab === 'api-keys' ? 'active' : ''}`}
            onClick={() => { setActiveTab(activeTab === 'api-keys' ? null : 'api-keys'); setSidebarOpen(false); }}
          >
            <i className="fas fa-key"></i> <span className="nav-tab-label">API Keys</span>
          </button>
          <button
            className={`nav-tab ${activeTab === 'tools' ? 'active' : ''}`}
            onClick={() => { setActiveTab(activeTab === 'tools' ? null : 'tools'); setSidebarOpen(false); }}
          >
            <i className="fas fa-wrench"></i> <span className="nav-tab-label">Tools</span>
          </button>
          <button
            className={`nav-tab ${activeTab === 'waterfalls' ? 'active' : ''}`}
            onClick={() => { setActiveTab(activeTab === 'waterfalls' ? null : 'waterfalls'); setSidebarOpen(false); }}
          >
            <i className="fas fa-water"></i> <span className="nav-tab-label">Waterfalls</span>
          </button>
        </div>
        
        <div className="nav-right">
          <StatusIndicator status={ollamaStatus} />
          <button
            type="button"
            className="btn-theme-toggle"
            onClick={handleThemeToggle}
            title="Toggle theme (Shift+Click resets to system default)"
            aria-label={`Activate ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            <i className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'}`} aria-hidden="true"></i>
          </button>
          <span className="username">
            <i className="fas fa-user-circle" aria-hidden="true"></i>
            <span className="username-text">{user.username}</span>
          </span>
          <button onClick={handleLogout} className="btn-logout">Logout</button>
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className="content-wrapper">
        {/* Dimmed overlay behind the mobile conversation drawer */}
        <div
          className={`sidebar-overlay ${sidebarOpen ? 'sidebar-overlay--visible' : ''}`}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        ></div>

        {/* Sidebar with Conversations */}
        <div className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''}`}>
          <div className="sidebar-controls">
            <button className="btn-new-conversation" onClick={createNewConversation}>
              + New Chat
            </button>
            
            {ollamaStatus.models.length > 0 && (
              <div className="model-selector">
                <label>Model:</label>
                <select 
                  value={selectedModel} 
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="model-select"
                >
                  {ollamaStatus.models.map(model => (
                    <option key={model.name} value={model.name}>
                      {model.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          <ConversationList
            conversations={conversations}
            currentConversation={currentConversation}
            onSelect={selectConversation}
            onDelete={deleteConversation}
            onUpdateTitle={updateConversationTitle}
          />
        </div>
        
        {/* Main Content Area - Shows Tab Content or Chat */}
        <div className="main-content">
          {activeTab === 'api-keys' && (
            <APIKeyManager
              onClose={() => setActiveTab(null)}
            />
          )}
          
          {activeTab === 'tools' && (
            <ToolInstanceManager
              focusedToolId={focusedToolId}
              toolEditId={toolEditId}
              onClearFocus={() => setFocusedToolId(null)}
              onClearToolEdit={() => setToolEditId(null)}
            />
          )}
          
          {activeTab === 'waterfalls' && (
            <WaterfallBuilder onNavigateToTool={handleNavigateToTool} />
          )}
          
          {!activeTab && (
            <ChatWindow
              conversation={currentConversation}
              selectedModel={selectedModel}
              selectedPrompt={selectedPrompt}
              onPromptUsed={() => setSelectedPrompt(null)}
              onMessageSent={() => {
                loadConversations();
                if (currentConversation) {
                  selectConversation(currentConversation.id);
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
